import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';
// @ts-ignore
import pdfWorker from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';
import { Diagnostics, GoldenPathStep } from './diagnostics';
import { PILOT_MODE, PILOT_LIMITS } from '../config/pilot';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

export async function validateDocument(file: File, base64Data: string): Promise<{ isValid: boolean, error?: string }> {
    const maxPdfMb = PILOT_MODE ? PILOT_LIMITS.PDF_MAX_SIZE_MB : 100;
    const maxDocxMb = PILOT_MODE ? PILOT_LIMITS.WORD_MAX_SIZE_MB : 100;
    const maxPages = PILOT_MODE ? 75 : 100;

    const pdfSizeLimitError = PILOT_MODE 
        ? `This file is too large for the current Lumina beta pilot. Please use a file of ${maxPdfMb} MB or less, or contact Lumina support for assisted setup.`
        : `This PDF is too large for the current MVP. Please use a PDF file of ${maxPdfMb} MB or less, or split the document into smaller sections.`;
    const docxSizeLimitError = PILOT_MODE 
        ? `This file is too large for the current Lumina beta pilot. Please use a file of ${maxDocxMb} MB or less, or contact Lumina support for assisted setup.`
        : `This Word document is too large. Please use a DOCX file of ${maxDocxMb} MB or less.`;
    const pdfPagesLimitError = PILOT_MODE
        ? `This file is too large for the current Lumina beta pilot. Please use a PDF with ${maxPages} pages or fewer.`
        : `This PDF has too many pages. Please use a PDF with ${maxPages} pages or fewer.`;
    const cannotReadError = "We could not read this file from your device. This can happen with iCloud files, scanned PDFs, unsupported file types, or browser memory limits. Please try downloading the file locally first, using a smaller readable file, or uploading from a desktop browser.";

    Diagnostics.logStep(GoldenPathStep.DOCUMENT_UPLOAD, 'success', {
        name: file.name,
        size: file.size,
        type: file.type,
        userAgent: navigator.userAgent
    });

    if (file.size === 0) {
        Diagnostics.logStep(GoldenPathStep.DOCUMENT_UPLOAD, 'fail', { name: file.name, size: file.size, error: 'empty_file' });
        return { isValid: false, error: cannotReadError };
    }

    try {
        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        
        if (isPdf) {
            if (file.size > maxPdfMb * 1024 * 1024) {
                 Diagnostics.logStep(GoldenPathStep.DOCUMENT_UPLOAD, 'fail', { name: file.name, size: file.size, type: isPdf ? 'pdf' : 'other', error: 'file_too_large_pdf' });
                 return { isValid: false, error: pdfSizeLimitError };
            }

            const binaryString = atob(base64Data);
            const len = binaryString.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            
            const loadingTask = pdfjsLib.getDocument({ data: bytes });
            const doc = await loadingTask.promise;
            
            if (doc.numPages > maxPages) {
                Diagnostics.logStep(GoldenPathStep.DOCUMENT_UPLOAD, 'fail', { name: file.name, pages: doc.numPages, type: 'pdf', error: 'too_many_pages' });
                return { isValid: false, error: pdfPagesLimitError };
            }
            
            // Check for scanned or unreadable PDFs
            // We sample the first few pages (up to 3) to see if there's textual content
            let totalTextLength = 0;
            const pagesToSample = Math.min(doc.numPages, 3);
            
            for (let i = 1; i <= pagesToSample; i++) {
                const page = await doc.getPage(i);
                const content = await page.getTextContent();
                const pageText = content.items.map((item: any) => item.str).join(' ').trim();
                totalTextLength += pageText.length;
            }
            
            // If less than 50 characters extracted across up to 3 pages, it's likely scanned
            if (totalTextLength < 50) {
                Diagnostics.logStep(GoldenPathStep.DOCUMENT_UPLOAD, 'fail', { name: file.name, pages: doc.numPages, type: 'pdf', error: 'unreadable_scanned' });
                return { 
                    isValid: false, 
                    error: cannotReadError 
                };
            }
            
        } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || file.name.toLowerCase().endsWith('.docx')) {
            if (file.size > maxDocxMb * 1024 * 1024) {
               Diagnostics.logStep(GoldenPathStep.DOCUMENT_UPLOAD, 'fail', { name: file.name, size: file.size, type: 'docx', error: 'file_too_large_docx' });
               return { isValid: false, error: docxSizeLimitError };
            }
        }
    } catch (err: any) {
        console.error("Document validation failed:", err);
        Diagnostics.logStep(GoldenPathStep.DOCUMENT_UPLOAD, 'fail', { 
            errorMsg: err.message, 
            name: file.name, 
            size: file.size, 
            type: file.type 
        });
        return { isValid: false, error: cannotReadError };
    }

    return { isValid: true };
}
